/**
 * Shared react-select styling so every react-select / CreatableSelect in the app
 * matches the design tokens (surface, border, control height/radius, focus ring)
 * and dark-modes automatically via the CSS variables. Spread into a select:
 *
 *   import { reactSelectStyles } from "~/components/ui";
 *   <Select styles={reactSelectStyles} ... />
 *
 * Colors reference the same CSS variables as the rest of the design system, so
 * they flip in dark mode with no extra config. Pass a merge if a call site needs
 * tweaks: `styles={{ ...reactSelectStyles, control: (b,s)=>({...}) }}`.
 */
const surface = "rgb(var(--surface-base))";
const surfaceSubtle = "rgb(var(--surface-subtle))";
const borderSubtle = "rgb(var(--border-subtle))";
const secondary = "rgb(var(--color-secondary))";
const text = "rgb(var(--text-primary))";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const reactSelectStyles: any = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: "2.5rem",
    backgroundColor: surface,
    borderRadius: "0.75rem",
    borderColor: state.isFocused ? secondary : borderSubtle,
    boxShadow: state.isFocused
      ? `0 0 0 2px rgb(var(--color-secondary) / 0.15)`
      : "none",
    "&:hover": { borderColor: secondary },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "2px 10px" }),
  singleValue: (base: any) => ({ ...base, color: text }),
  input: (base: any) => ({ ...base, color: text }),
  placeholder: (base: any) => ({
    ...base,
    color: "rgb(var(--text-primary) / 0.4)",
  }),
  menu: (base: any) => ({
    ...base,
    backgroundColor: surface,
    borderRadius: "1rem",
    border: `1px solid ${borderSubtle}`,
    boxShadow:
      "0 4px 8px -2px rgb(20 47 64 / 0.07), 0 16px 36px -8px rgb(20 47 64 / 0.11)",
    overflow: "hidden",
    zIndex: 1400,
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 1400 }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? surfaceSubtle
      : state.isFocused
      ? surfaceSubtle
      : "transparent",
    color: state.isSelected ? secondary : text,
    fontWeight: state.isSelected ? 600 : 400,
    cursor: "pointer",
    "&:active": { backgroundColor: surfaceSubtle },
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: surfaceSubtle,
    borderRadius: "9999px",
  }),
  multiValueLabel: (base: any) => ({ ...base, color: text }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: text,
    borderRadius: "9999px",
    "&:hover": {
      backgroundColor: "rgb(var(--danger) / 0.15)",
      color: "rgb(var(--danger))",
    },
  }),
  indicatorSeparator: (base: any) => ({
    ...base,
    backgroundColor: borderSubtle,
  }),
  dropdownIndicator: (base: any) => ({
    ...base,
    color: "rgb(var(--text-primary) / 0.6)",
  }),
  clearIndicator: (base: any) => ({
    ...base,
    color: "rgb(var(--text-primary) / 0.6)",
  }),
};
